import { z } from 'zod';

/**
 * THE VOCABULARY OF THE PLAYER ACTIVITY LOG.
 *
 * One list of kinds, one list of outcomes, one list of reasons, and the words
 * operators and players read for each. The words are decided once, here; no
 * screen writes its own. A kind never reaches a screen as its code.
 *
 * `done` is the sentence for a row that succeeded; `tried` is for every other
 * outcome, so a refused or failed row never claims the act happened.
 */
export const PLAYER_ACTIVITY_KIND_WORDS = {
  SIGN_IN: { done: 'Signed in', tried: 'Tried to sign in' },
  SIGN_OUT: { done: 'Signed out', tried: 'Tried to sign out' },
  REGISTERED: { done: 'Opened the account', tried: 'Tried to open the account' },
  EMAIL_CONFIRMED: {
    done: 'Confirmed the email address',
    tried: 'Tried to confirm the email address',
  },
  EMAIL_LINK_ASKED: {
    done: 'Asked for a new confirmation link',
    tried: 'Tried to ask for a new confirmation link',
  },
  PASSWORD_RESET_ASKED: {
    done: 'Asked for a password reset link',
    tried: 'Tried to ask for a password reset link',
  },
  PASSWORD_RESET: {
    done: 'Set a new password from a reset link',
    tried: 'Tried to set a new password from a reset link',
  },
  PASSWORD_CHANGED: { done: 'Changed the password', tried: 'Tried to change the password' },
  SIGNED_OUT_EVERYWHERE: { done: 'Signed out everywhere', tried: 'Tried to sign out everywhere' },
  TWO_FACTOR_SETUP: {
    done: 'Started setting up two-factor',
    tried: 'Tried to set up two-factor',
  },
  TWO_FACTOR_ON: { done: 'Switched two-factor on', tried: 'Tried to switch two-factor on' },
  TWO_FACTOR_OFF: { done: 'Switched two-factor off', tried: 'Tried to switch two-factor off' },
  RECOVERY_CODES: { done: 'Made new recovery codes', tried: 'Tried to make new recovery codes' },
  DEPOSIT_STARTED: { done: 'Started a deposit', tried: 'Tried to start a deposit' },
  DEPOSIT_CHECKED: { done: 'Checked on a deposit', tried: 'Tried to check on a deposit' },
  WITHDRAWAL_ASKED: { done: 'Asked to withdraw', tried: 'Tried to withdraw' },
  PAYOUT_ACCOUNT_ADDED: { done: 'Added a payout account', tried: 'Tried to add a payout account' },
  PAYOUT_ACCOUNT_REMOVED: {
    done: 'Removed a payout account',
    tried: 'Tried to remove a payout account',
  },
  GAME_OPENED: { done: 'Opened a game', tried: 'Tried to open a game' },
  LIMIT_SET: { done: 'Set a deposit, loss or betting limit', tried: 'Tried to set a limit' },
  SELF_EXCLUDED: { done: 'Excluded themselves', tried: 'Tried to exclude themselves' },
  IDENTITY_SENT: { done: 'Sent identity documents', tried: 'Tried to send identity documents' },
  IDENTITY_LINK_ASKED: {
    done: 'Asked for a new photo link',
    tried: 'Tried to ask for a new photo link',
  },
  IDENTITY_TO_A_PERSON: {
    done: 'Asked a person to check their identity',
    tried: 'Tried to ask a person to check their identity',
  },
  IDENTITY_CORRECTED: {
    done: 'Corrected their name or date of birth',
    tried: 'Tried to correct their name or date of birth',
  },
  SUPPORT_TICKET: { done: 'Opened a support ticket', tried: 'Tried to open a support ticket' },
  SUPPORT_REPLY: {
    done: 'Replied on a support ticket',
    tried: 'Tried to reply on a support ticket',
  },
  SUPPORT_FILE: {
    done: 'Added a file to a support ticket',
    tried: 'Tried to add a file to a support ticket',
  },
  POINTS_REDEEMED: { done: 'Redeemed points', tried: 'Tried to redeem points' },
  PROMOTION_JOINED: { done: 'Joined a promotion', tried: 'Tried to join a promotion' },
  MARKETING_CHOICE: {
    done: 'Changed marketing preferences',
    tried: 'Tried to change marketing preferences',
  },
  STAFF_SIGNED_OUT: { done: 'Signed out by staff', tried: 'Staff tried to sign them out' },
  STAFF_PASSWORD_RESET: {
    done: 'Password taken away by staff',
    tried: 'Staff tried to take the password away',
  },
  STAFF_LINK_RESENT: {
    done: 'Confirmation link re-sent by staff',
    tried: 'Staff tried to re-send the confirmation link',
  },
  STAFF_SUSPENDED: {
    done: 'Suspended by staff and signed out',
    tried: 'Staff tried to suspend the account',
  },
  REQUEST: { done: 'Made a request', tried: 'Tried a request' },
} as const satisfies Record<string, { done: string; tried: string }>;

type KindName = keyof typeof PLAYER_ACTIVITY_KIND_WORDS;

export const playerActivityKind = z.enum(Object.keys(PLAYER_ACTIVITY_KIND_WORDS) as [KindName, ...KindName[]]);
export type PlayerActivityKind = z.infer<typeof playerActivityKind>;

/** The same five as the CHECK player_activity_outcome_known in sql/1029; a test compares them. */
export const playerActivityOutcome = z.enum(['SUCCEEDED', 'FAILED', 'REFUSED', 'LOCKED', 'ERROR']);
export type PlayerActivityOutcome = z.infer<typeof playerActivityOutcome>;

export const PLAYER_ACTIVITY_OUTCOME_WORDS: Record<PlayerActivityOutcome, string> = {
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
  REFUSED: 'Refused',
  LOCKED: 'Locked out',
  ERROR: 'Did not finish',
};

export const playerActivityReason = z.enum(['BAD_PASSWORD', 'NO_PASSWORD', 'LOCKED', 'SUSPENDED']);
export type PlayerActivityReason = z.infer<typeof playerActivityReason>;

export const PLAYER_ACTIVITY_REASON_WORDS: Record<PlayerActivityReason, string> = {
  BAD_PASSWORD: 'Wrong password',
  NO_PASSWORD: 'This account has no password yet',
  LOCKED: 'Too many wrong passwords, so sign-in was locked for a while',
  SUSPENDED: 'The account is suspended',
};

/**
 * One of the player's own recent sign-ins. The address is already coarse; the full
 * address, the browser string and the request id are never part of it.
 */
export interface PlayerSignInRow {
  id: string;
  at: string;
  outcome: PlayerActivityOutcome;
  reason: PlayerActivityReason | null;
  device: string | null;
  approximateAddress: string | null;
  thisSession: boolean;
}

/**
 * The CLOSED shape of a row's detail. Built only through the API's
 * boundedDetail(); never a request body, password, code, token or cookie.
 */
export interface PlayerActivityDetail {
  status?: number;
  reason?: PlayerActivityReason;
  by?: { staffId: string; role: string };
  sessionsEnded?: number;
  route?: string;
}

/** How often, at most, a player's last-seen moment is refreshed. */
export const PLAYER_LAST_SEEN_INTERVAL_SECONDS = 300;
/** How many recent sign-ins a player sees on their own Security page. */
export const PLAYER_RECENT_SIGN_INS_LIMIT = 20;

/* ── What staff read, and what they export ─────────────────────────────── */

/** A page of a player's log on the staff screen: this many rows unless asked, and never more than the max. */
export const PLAYER_ACTIVITY_PAGE_DEFAULT = 50;
export const PLAYER_ACTIVITY_PAGE_MAX = 200;

/**
 * A Manila calendar day, YYYY-MM-DD. The one copy: the games provider lists
 * read it too, through services/api/src/modules/games/list-cursor.ts.
 *
 * And a day the calendar has: `2026-02-31` fits the pattern, and the date
 * arithmetic that turns a day into its Manila bounds would quietly read it as
 * 3 March — and the database, handed it as a date, refuses it, which a list
 * answered as a server error rather than as the caller's mistake.
 */
export const manilaDay = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Dates are YYYY-MM-DD (Manila).')
  .refine((day) => {
    const [year, month, date] = day.split('-').map(Number) as [number, number, number];
    const built = new Date(Date.UTC(year, month - 1, date));
    return (
      built.getUTCFullYear() === year &&
      built.getUTCMonth() === month - 1 &&
      built.getUTCDate() === date
    );
  }, 'That day is not on the calendar.');

/** GET /api/players/:id/activity. Days are Manila days, both ends included. */
export const playerActivityListQuery = z.object({
  kind: playerActivityKind.optional(),
  outcome: playerActivityOutcome.optional(),
  from: manilaDay.optional(),
  to: manilaDay.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(PLAYER_ACTIVITY_PAGE_MAX)
    .default(PLAYER_ACTIVITY_PAGE_DEFAULT),
});
export type PlayerActivityListQuery = z.infer<typeof playerActivityListQuery>;

/** POST /api/players/:id/activity/export. A file always names its days, so both are required. */
export const playerActivityExportRequest = z.object({
  from: manilaDay,
  to: manilaDay,
  kind: playerActivityKind.optional(),
  outcome: playerActivityOutcome.optional(),
});
export type PlayerActivityExportRequest = z.infer<typeof playerActivityExportRequest>;

/**
 * One row of a player's log as staff read it. Everything, by decision D3: the
 * full address, the raw browser string and the request id. `redacted` is true
 * when the retention job blanked the address and browser, so a missing one
 * reads as removed rather than as a gap.
 */
export interface StaffActivityRow {
  id: string;
  /** The instant, ISO 8601 in UTC; the screen shows it in Manila time. */
  at: string;
  kind: PlayerActivityKind;
  outcome: PlayerActivityOutcome;
  ip: string | null;
  userAgent: string | null;
  device: string | null;
  sessionId: string | null;
  requestId: string | null;
  detail: PlayerActivityDetail;
  redacted: boolean;
}
